import {
  DevicesAvailabilityState,
  DevicesDeviceType,
  getGetDeviceQueryKey,
  getListDevicesQueryKey,
  useGetDevice,
  useListDevices,
  useUpdateDeviceState,
  type DevicesDevice,
  type DevicesDeviceState,
} from '@smarthome/api';
import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check, ChevronDown, Lightbulb, Power } from 'lucide-react';
import { memo, useCallback, useMemo, useState } from 'react';
import { z } from 'zod';

import type { ConfigFormProps, RendererProps, WidgetDefinition } from '../types';

import { cn } from '@/components/ui/utils';
import { DeviceControlModal } from '@/features/devices/components/DeviceControlModal';
import {
  applyStatePatch,
  getDeviceIcon,
  getDeviceIdForApi,
  getDeviceOn,
} from '@/features/devices/utils';

/**
 * Device widget config schema
 */
export const deviceConfigSchema = z.object({
  deviceHumanId: z.string().min(1, 'Device is required'),
});

export type DeviceConfig = z.infer<typeof deviceConfigSchema>;

/**
 * Config form for Device widget
 */
function DeviceConfigForm({ config, onChange, errors }: ConfigFormProps<DeviceConfig>) {
  const [isOpen, setIsOpen] = useState(false);
  const { data: devicesData, isLoading } = useListDevices();
  const devices = devicesData?.devices ?? [];

  const selectedDevice = devices.find((d) => d.humanId === config.deviceHumanId);

  return (
    <div className="space-y-4">
      <div>
        <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-zinc-300">
          Device
        </label>
        <div className="relative">
          <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            className={`flex w-full items-center justify-between rounded-lg border px-3 py-2.5 text-left text-sm transition-colors ${
              errors?.['deviceHumanId']
                ? 'border-red-300 bg-red-50/50 dark:border-red-500/30 dark:bg-red-500/10'
                : 'border-gray-200 bg-white hover:border-gray-300 dark:border-white/[0.08] dark:bg-zinc-900 dark:hover:border-white/[0.12]'
            }`}
          >
            <span
              className={
                selectedDevice
                  ? 'text-gray-900 dark:text-white'
                  : 'text-gray-500 dark:text-zinc-500'
              }
            >
              {selectedDevice?.name || selectedDevice?.humanId || 'Select a device...'}
            </span>
            <ChevronDown
              className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            />
          </button>

          {isOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
              <div className="absolute left-0 right-0 z-20 mt-1 max-h-60 overflow-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-white/[0.08] dark:bg-zinc-900">
                {isLoading ? (
                  <div className="px-3 py-2 text-sm text-gray-500 dark:text-zinc-500">
                    Loading devices...
                  </div>
                ) : devices.length === 0 ? (
                  <div className="px-3 py-2 text-sm text-gray-500 dark:text-zinc-500">
                    No devices available
                  </div>
                ) : (
                  devices.map((device) => {
                    const DeviceIcon = getDeviceIcon(device.type, device.subtype);
                    const isOn = getDeviceOn(device);
                    return (
                      <button
                        key={device.id}
                        type="button"
                        onClick={() => {
                          onChange({ ...config, deviceHumanId: device.humanId ?? '' });
                          setIsOpen(false);
                        }}
                        className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-white/[0.04]"
                      >
                        <div className="flex items-center gap-2">
                          <DeviceIcon
                            className={`h-4 w-4 ${isOn ? 'text-emerald-500' : 'text-gray-400 dark:text-zinc-500'}`}
                          />
                          <div>
                            <div className="text-gray-900 dark:text-white">
                              {device.name || device.humanId}
                            </div>
                            {device.name && device.humanId && (
                              <div className="text-xs text-gray-500 dark:text-zinc-500">
                                {device.humanId}
                              </div>
                            )}
                          </div>
                        </div>
                        {device.humanId === config.deviceHumanId && (
                          <Check className="h-4 w-4 text-blue-500" />
                        )}
                      </button>
                    );
                  })
                )}
              </div>
            </>
          )}
        </div>
        {errors?.['deviceHumanId'] && (
          <p className="mt-1.5 flex items-center gap-1 text-sm text-red-600 dark:text-red-400">
            <AlertTriangle className="h-3.5 w-3.5" />
            {errors['deviceHumanId']}
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * Hook for updating device state with optimistic updates
 */
function useDeviceStateUpdate() {
  const queryClient = useQueryClient();

  const { mutate: updateDeviceState } = useUpdateDeviceState({
    mutation: {
      onMutate: async ({ id, data: stateData }) => {
        await queryClient.cancelQueries({ queryKey: getListDevicesQueryKey() });

        const previousData = queryClient.getQueryData<{ devices: DevicesDevice[] }>(
          getListDevicesQueryKey()
        );

        // Optimistic update for list view
        queryClient.setQueryData<{ devices: DevicesDevice[] }>(getListDevicesQueryKey(), (old) => {
          if (!old?.devices) return old;
          return {
            ...old,
            devices: old.devices.map((d) => {
              if (d.id === id || d.humanId === id) {
                return {
                  ...d,
                  state: applyStatePatch(d.state, stateData.state),
                };
              }
              return d;
            }),
          };
        });

        // Also update device detail cache if present
        const detailKey = getGetDeviceQueryKey(id);
        const previousDetail = queryClient.getQueryData<{ device: DevicesDevice }>(detailKey);
        if (previousDetail) {
          queryClient.setQueryData<{ device: DevicesDevice }>(detailKey, (old) => {
            if (!old?.device) return old;
            return {
              ...old,
              device: {
                ...old.device,
                state: applyStatePatch(old.device.state, stateData.state),
              },
            };
          });
        }

        return { previousData, previousDetail };
      },
      onError: (_err, { id }, context) => {
        // Rollback on error
        if (context?.previousData) {
          queryClient.setQueryData(getListDevicesQueryKey(), context.previousData);
        }
        if (context?.previousDetail) {
          queryClient.setQueryData(getGetDeviceQueryKey(id), context.previousDetail);
        }
      },
      onSettled: (_data, _err, { id }) => {
        // Invalidate to ensure fresh data
        queryClient.invalidateQueries({ queryKey: getGetDeviceQueryKey(id) });
        queryClient.invalidateQueries({ queryKey: getListDevicesQueryKey() });
      },
    },
  });

  return updateDeviceState;
}

/**
 * Inline multi-position switch controls
 */
interface MultiPositionControlsProps {
  device: DevicesDevice;
  onUpdateState: (state: DevicesDeviceState) => void;
  disabled?: boolean;
  columns: number;
}

function MultiPositionControls({
  device,
  onUpdateState,
  disabled,
  columns,
}: MultiPositionControlsProps) {
  const positions = device.capabilities?.switch?.positions ?? [];
  const currentPosition = device.state?.switch?.position;

  const handlePositionSelect = (e: React.MouseEvent, positionId: string) => {
    e.stopPropagation();
    onUpdateState({ switch: { position: positionId } });
  };

  const rows = Math.ceil(positions.length / columns);

  return (
    <div
      className="grid h-full gap-2"
      style={{
        gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
        gridTemplateRows: `repeat(${rows}, minmax(0, 1fr))`,
      }}
    >
      {positions.map((position) => {
        const isSelected = currentPosition === position.id;
        return (
          <button
            key={position.id}
            type="button"
            onClick={(e) => handlePositionSelect(e, position.id!)}
            disabled={disabled}
            className={cn(
              'flex items-center justify-center rounded-lg px-3 text-sm font-medium transition-all',
              isSelected
                ? 'bg-blue-500 text-white shadow-sm'
                : 'bg-gray-50 text-gray-700 hover:bg-gray-100 dark:bg-zinc-800/50 dark:text-zinc-300 dark:hover:bg-zinc-800',
              disabled && 'cursor-not-allowed opacity-50'
            )}
            title={position.label || position.id}
          >
            <span className="truncate">{position.label || position.id}</span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * Renderer for Device widget
 */
const DeviceRenderer = memo(function DeviceRenderer({
  widget,
  config,
}: RendererProps<DeviceConfig>) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const updateDeviceState = useDeviceStateUpdate();

  const {
    data: deviceData,
    isLoading,
    isError,
  } = useGetDevice(config.deviceHumanId, {
    query: {
      enabled: !!config.deviceHumanId,
      refetchInterval: 10000,
    },
  });

  const device = deviceData?.device;
  const DeviceIcon = device ? getDeviceIcon(device.type, device.subtype) : Lightbulb;
  const isOn = device ? getDeviceOn(device) : false;
  const isAvailable = device?.availability === DevicesAvailabilityState.AvailabilityAvailable;

  // Check if device is a multi-position switch
  const isMultiPositionSwitch = useMemo(() => {
    if (!device) return false;
    if (device.type !== DevicesDeviceType.DeviceTypeSwitch) return false;
    const positions = device.capabilities?.switch?.positions ?? [];
    return positions.length > 0;
  }, [device]);

  // Calculate optimal grid columns based on widget size and position count
  const gridColumns = useMemo(() => {
    if (!device) return 2;
    const positions = device.capabilities?.switch?.positions ?? [];
    const posCount = positions.length;
    const w = widget.w ?? 2;
    const h = widget.h ?? 2;

    // Aim for roughly square cells by balancing columns vs rows
    // based on widget aspect ratio
    const aspectRatio = w / h;

    if (posCount === 1) return 1;
    if (posCount === 2) return aspectRatio >= 1.5 ? 2 : 1;
    if (posCount === 3) return aspectRatio >= 2 ? 3 : aspectRatio >= 1 ? 2 : 1;
    if (posCount === 4) return aspectRatio >= 2 ? 4 : 2;
    if (posCount <= 6) return aspectRatio >= 1.5 ? 3 : 2;
    return Math.ceil(Math.sqrt(posCount * aspectRatio));
  }, [device, widget.w, widget.h]);

  // Determine if we have space for inline controls
  const showInlineControls = useMemo(() => {
    if (!isMultiPositionSwitch || !device) return false;
    const positions = device.capabilities?.switch?.positions ?? [];
    const w = widget.w ?? 2;
    const h = widget.h ?? 2;

    // Always show inline controls if we have reasonable space
    if (w === 1 && h === 1 && positions.length > 4) return false;
    if (w === 1 && positions.length > 8) return false;

    return true;
  }, [isMultiPositionSwitch, device, widget.w, widget.h]);

  const handleUpdateState = useCallback(
    (state: DevicesDeviceState) => {
      if (!device) return;
      const deviceId = getDeviceIdForApi(device);
      if (!deviceId) return;
      updateDeviceState({ id: deviceId, data: { state } });
    },
    [device, updateDeviceState]
  );

  if (!config.deviceHumanId) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-sm text-gray-500 dark:text-zinc-500">No device configured</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500 dark:border-zinc-700" />
      </div>
    );
  }

  if (isError || !device) {
    return (
      <div className="flex h-full flex-col items-center justify-center text-center">
        <AlertTriangle className="mb-2 h-5 w-5 text-amber-500" />
        <p className="text-xs text-gray-500 dark:text-zinc-500">Device not found</p>
        <p className="mt-0.5 font-mono text-[10px] text-gray-400 dark:text-zinc-600">
          {config.deviceHumanId}
        </p>
      </div>
    );
  }

  // Multi-position switch with inline controls
  if (showInlineControls && isMultiPositionSwitch) {
    if (!isAvailable) {
      return (
        <div className="flex h-full w-full items-center justify-center">
          <p className="text-xs text-amber-600 dark:text-amber-400">Unavailable</p>
        </div>
      );
    }

    return (
      <MultiPositionControls
        device={device}
        onUpdateState={handleUpdateState}
        disabled={!isAvailable}
        columns={gridColumns}
      />
    );
  }

  // Standard device widget (non-multi-position or fallback)
  return (
    <>
      <div className="flex h-full flex-col items-center justify-center">
        <button
          onClick={() => setIsModalOpen(true)}
          className={`flex h-14 w-14 items-center justify-center rounded-full transition-all ${
            isOn
              ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/30 hover:bg-emerald-600'
              : 'bg-gray-100 text-gray-500 hover:bg-gray-200 dark:bg-zinc-800 dark:text-zinc-400 dark:hover:bg-zinc-700'
          } ${!isAvailable ? 'opacity-50' : ''}`}
        >
          <DeviceIcon className="h-6 w-6" />
        </button>
        <p className="mt-2 text-xs font-medium text-gray-900 dark:text-white">
          {device.name || device.humanId}
        </p>
        {!isAvailable && (
          <p className="mt-0.5 text-[10px] text-amber-600 dark:text-amber-400">Unavailable</p>
        )}
      </div>

      <DeviceControlModal
        device={device}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </>
  );
});

/**
 * Device widget definition
 */
export const deviceWidget: WidgetDefinition<DeviceConfig> = {
  type: 'device',
  displayName: 'Device Control',
  description: 'Control a single device with full controls',
  icon: Power,
  defaultSize: { w: 2, h: 2 },
  minSize: { w: 1, h: 1 },
  maxSize: { w: 3, h: 3 },
  configSchema: deviceConfigSchema,
  defaultConfig: {
    deviceHumanId: '',
  },
  ConfigForm: DeviceConfigForm,
  Renderer: DeviceRenderer,
};
